// CMF card — the arithmetic behind one site's countermeasure recommendation,
// shown inside the Deficiency-sites expander and the Program table. Shared so
// the two never disagree.
import React from 'react';
import { fmtUsd } from './insightsText';
import { pvFactor, starsText } from './cmfBenefit';

const CLEARINGHOUSE = 'https://cmfclearinghouse.fhwa.dot.gov/detail.php?facid=';

export function Stars({ entry }) {
  if (!entry) return <span className="safety-muted">—</span>;
  return (
    <>
      <span className="safety-stars" title={`Clearinghouse star quality rating: ${entry.stars} of 5`}>
        {starsText(entry.stars)}
      </span>
      {entry.fallback && (
        <span className="safety-fallback" title="No 4–5★ CMF exists for this analysis; a 3★ study is used">3★</span>
      )}
    </>
  );
}

function Row({ rec, entryRow, exposure, ratePct }) {
  const e = entryRow.entry;
  const mix = Object.entries(entryRow.matching).filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${k}`).join(', ') || 'none';
  return (
    <div className="safety-cmf-card">
      <div>
        <h4>{e.name}</h4>
        <div className="src">
          FHWA CMF Clearinghouse ID <a href={`${CLEARINGHOUSE}${e.crfid}`} target="_blank" rel="noreferrer">{e.crfid}</a>
          {' · '}<Stars entry={e} />{e.se != null ? ` · SE ${e.se}` : ''}
          {' · '}{e.study}
        </div>
        <div className="src">Applies to {e.appliesTo}. Matching crashes at this site over {exposure} yr: <code>{mix}</code>.</div>
        {rec.note && <div className="src safety-cmf-note">{rec.note}</div>}
      </div>
      <dl className="safety-kv">
        <dt>CMF</dt><dd>{e.cmf}{e.cmfBySeverity ? ` (K ${e.cmfBySeverity.K})` : ''}</dd>
        <dt>Crash reduction</dt><dd>{Math.round((1 - e.cmf) * 100)}%</dd>
        <dt>Prevented / yr</dt><dd>{entryRow.preventedPerYr}</dd>
        <dt>Benefit / yr</dt><dd className="safety-benefit">{fmtUsd(entryRow.benefitPerYr)}</dd>
        <dt>Service life</dt><dd>{entryRow.life} yr</dd>
        <dt>PV @ {ratePct}%</dt><dd>{fmtUsd(entryRow.pvBenefit)}</dd>
        <dt>Unit cost</dt><dd>{fmtUsd(entryRow.unitCost)} <span className="safety-muted">({e.unitCostNote || 'placeholder'})</span></dd>
        <dt>B/C</dt><dd className={entryRow.bcRatio >= 1 ? 'safety-bc-good' : 'safety-bc-weak'}>{entryRow.bcRatio ?? '—'}</dd>
      </dl>
      <div className="calc">
        <code>{entryRow.matchCount} ÷ {exposure} yr × (1 − {e.cmf}) = {entryRow.preventedPerYr} prevented/yr</code>
        {' · '}
        <code>× PV factor {pvFactor(ratePct, entryRow.life).toFixed(2)} = {fmtUsd(entryRow.pvBenefit)}</code>
      </div>
    </div>
  );
}

export default function CmfCard({ rec, exposure, ratePct }) {
  if (!rec) return null;
  if (!rec.entry) {
    return <div className="safety-cmf-card safety-cmf-card-empty"><div className="src">{rec.note}</div></div>;
  }
  return (
    <div className="safety-cmf-stack">
      <Row rec={rec} entryRow={rec} exposure={exposure} ratePct={ratePct} />
      {(rec.alternatives || []).length > 0 && (
        <>
          <div className="safety-muted safety-cmf-alt-label">Alternatives under the same policy</div>
          {rec.alternatives.map((alt) => (
            <Row key={alt.entry.crfid} rec={{}} entryRow={alt} exposure={exposure} ratePct={ratePct} />
          ))}
        </>
      )}
    </div>
  );
}
